"use client";

import { useCallback, useMemo, useState } from "react";

import {
  type CustomOptionDef,
  type PersonalisationValues,
  validatePersonalisation,
} from "~/server/api/schemas/personalisation";

export interface PersonalisationState {
  /** The product's definitions, normalised to an array (never nullish). */
  defs: CustomOptionDef[];
  /** Raw values keyed by definition id — the add-to-cart payload (P3-08). */
  values: PersonalisationValues;
  setValue: (id: string, value: string | boolean) => void;
  /** Fields the shopper has interacted with — gates when errors surface. */
  touched: Record<string, boolean>;
  markTouched: (id: string) => void;
  /** Validation errors by definition id (present whether or not touched). */
  errors: Record<string, string>;
  /** Live add-on delta for display (minor units); authoritative copy is server-side. */
  priceDeltaMinor: number;
  /** All required fields satisfied and no invalid values. */
  isValid: boolean;
}

/**
 * Personalisation state for a product's custom options (P3-07). Holds the raw
 * values and derives validity + the display price delta from the SAME validator
 * the server recomputes with, so the buy box and the eventual add-to-cart agree
 * on what's valid. Returns an inert, always-valid state when the product has no
 * custom options, so callers need no branching.
 */
export function usePersonalisation(
  // Nullable-tolerant: a PDP projection cached before P3-07 shipped can arrive
  // without `customOptions` (rollout/stale-cache), same guard as sizeGuideHtml.
  maybeDefs: readonly CustomOptionDef[] | null | undefined,
): PersonalisationState {
  const [values, setValues] = useState<PersonalisationValues>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const defs = useMemo(() => (maybeDefs ? [...maybeDefs] : []), [maybeDefs]);

  const setValue = useCallback((id: string, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [id]: value }));
  }, []);

  const markTouched = useCallback((id: string) => {
    setTouched((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  }, []);

  const result = useMemo(
    () => validatePersonalisation(defs, values),
    [defs, values],
  );

  return {
    defs,
    values,
    setValue,
    touched,
    markTouched,
    errors: result.ok ? {} : result.errors,
    priceDeltaMinor: result.priceDeltaMinor,
    isValid: result.ok,
  };
}
